import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldCombobox, FormFieldDropdown } from '~/components/FormField';
import { IconButton } from '~/components/IconButton';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import { RoleMap } from '~/modules/settings/constants/RoleMap';

type ExternalPermissionsProps = {
  name: string;
};

const EXTERNAL_USER_ROLES = [
  'LABEL_ADMIN',
  'VIDEO_COORDINATOR',
  'RELEASE_COORDINATOR',
] as const;

const mapRoles = (
  roles: Readonly<Array<keyof typeof RoleMap>>,
  t: (key: string) => string,
) =>
  roles.map((role) => ({
    value: role,
    label: t(RoleMap[role].name),
  }));

export const ExternalPermissions = ({ name }: ExternalPermissionsProps) => {
  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'roles' });
  const { t } = useTranslation('settings', { keyPrefix: 'user' });
  const { labelList } = useLabelList({});

  const { fields, append, remove } = useFieldArray({
    name,
  });

  const canRemove = fields.length > 1;

  const labels = labelList?.data ?? [];

  return (
    <ul className="flex flex-col gap-md">
      {fields.map((item, index) => (
        <li key={item.id} className="grid grid-cols-3 items-start gap-md">
          <FormFieldDropdown
            name={`${name}.${index}.role`}
            label={t('fields.role')}
            options={mapRoles(EXTERNAL_USER_ROLES, rolesT)}
          />
          <FormFieldCombobox
            name={`${name}.${index}.labels`}
            label={t('fields.labels')}
            options={labels.map((label) => ({
              label: label.name,
              value: label.id,
            }))}
            multiselect
            displaySelectedOptions
          />
          {canRemove ? (
            <IconButton
              iconType="trash"
              size="xlarge"
              className="mt-lg"
              onClick={() => remove(index)}
              aria-label={t('removeRole')}
            />
          ) : null}
        </li>
      ))}
      <li className="flex items-center">
        <IconButton
          iconType="plus"
          size="xlarge"
          onClick={() => {
            append({ role: '', labelGroup: '' });
          }}
        />
        {t('addRole')}
      </li>
    </ul>
  );
};

ExternalPermissions.displayName = 'ExternalPermissions';
